// Intake form response shape helpers (Fulfillment Slice 2).
//
// Slice 1 persisted tier_1_responses / tier_2_responses as opaque
// jsonb keyed by item id. Slice 2 adds typed read/write helpers so
// the per-item UI rows (and the server-side elected-count derivation
// in reviewForm) share one canonical shape:
//
//   tier_1_responses[id] = { pass, n_a_reason, notes }
//   tier_2_responses[id] = { elected, completed, notes }
//
// `pass` is tri-state in memory: true (passed), false (N/A — capture
// the reason), null (unanswered). The wire shape collapses null to
// "no entry under this key" so the form can be saved as a draft mid-
// fill without the per-item shape getting noisy.

import type { IntakeTemplateItem } from "./templateItem.js";

// ---------------------------------------------------------------------------
// Tier 1
// ---------------------------------------------------------------------------

export type Tier1Choice = "pass" | "na" | null;

export type Tier1Response = {
  /** true = passed, false = N/A (capture n_a_reason), null = unanswered. */
  pass: boolean | null;
  n_a_reason: string;
  notes: string;
};

const EMPTY_TIER_1: Tier1Response = { pass: null, n_a_reason: "", notes: "" };

export function emptyTier1Response(): Tier1Response {
  return { ...EMPTY_TIER_1 };
}

export function tier1ChoiceFrom(r: Tier1Response | null | undefined): Tier1Choice {
  if (!r || r.pass === null || r.pass === undefined) return null;
  return r.pass === true ? "pass" : "na";
}

/**
 * Coerce one slot of `form.tier_1_responses` (opaque from the server
 * boundary) into the strict in-memory shape. Defensive against shape
 * drift: missing fields default to empty strings; non-boolean `pass`
 * values normalize to null.
 */
export function coerceTier1Response(raw: unknown): Tier1Response {
  if (!raw || typeof raw !== "object") return emptyTier1Response();
  const o = raw as Record<string, unknown>;
  let pass: boolean | null = null;
  if (o.pass === true) pass = true;
  else if (o.pass === false) pass = false;
  return {
    pass,
    n_a_reason: typeof o.n_a_reason === "string" ? o.n_a_reason : "",
    notes: typeof o.notes === "string" ? o.notes : "",
  };
}

/** Apply a UI tri-state choice + free-text fields to produce a wire-ready response. */
export function tier1ResponseFromChoice(
  choice: Tier1Choice,
  n_a_reason: string,
  notes: string
): Tier1Response {
  if (choice === "pass") return { pass: true, n_a_reason: "", notes };
  if (choice === "na") return { pass: false, n_a_reason, notes };
  return { pass: null, n_a_reason: "", notes: "" };
}

// ---------------------------------------------------------------------------
// Tier 2
// ---------------------------------------------------------------------------

export type Tier2Response = {
  elected: boolean;
  /** Slice 2 leaves `completed` as carried-through but doesn't add UI for it. */
  completed: boolean;
  notes: string;
};

const EMPTY_TIER_2: Tier2Response = { elected: false, completed: false, notes: "" };

export function emptyTier2Response(): Tier2Response {
  return { ...EMPTY_TIER_2 };
}

export function coerceTier2Response(raw: unknown): Tier2Response {
  if (!raw || typeof raw !== "object") return emptyTier2Response();
  const o = raw as Record<string, unknown>;
  return {
    elected: o.elected === true,
    completed: o.completed === true,
    notes: typeof o.notes === "string" ? o.notes : "",
  };
}

/**
 * Count the number of Tier-2 items the reviewer (or filler) has
 * marked elected. Used by the server-side `reviewForm` to derive the
 * status routing (`statusAfterReview(electedCount)`) instead of
 * trusting a client-supplied number. Defensive: malformed entries
 * count as not-elected.
 */
export function countElectedTier2(
  responses: unknown
): number {
  if (!responses || typeof responses !== "object" || Array.isArray(responses)) {
    return 0;
  }
  let n = 0;
  for (const v of Object.values(responses as Record<string, unknown>)) {
    if (coerceTier2Response(v).elected) n += 1;
  }
  return n;
}

// ---------------------------------------------------------------------------
// UI hints
// ---------------------------------------------------------------------------

/**
 * Returns true iff every Tier-1 item in `items` has a definite answer
 * (pass=true or pass=false) in `responses`. Used by the fill modal to
 * disable the "Submit for review" button until all mandatory items
 * are answered. NOT enforced server-side — Slice 2 keeps the
 * server-side gate pluggable for follow-on slices.
 */
export function isTier1Complete(
  items: readonly IntakeTemplateItem[],
  responses: Record<string, unknown> | null | undefined
): boolean {
  if (items.length === 0) return true;
  if (!responses || typeof responses !== "object") return false;
  for (const it of items) {
    const r = coerceTier1Response((responses as Record<string, unknown>)[it.id]);
    if (r.pass === null) return false;
    if (r.pass === false && r.n_a_reason.trim().length === 0) return false;
  }
  return true;
}
